import { useEffect, useId, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';
import css from './ui/Sobreposicao.module.css';

/**
 * Diálogo modal.
 *
 * Usa o `<dialog>` nativo em vez de uma `div` com `position: fixed`: é ele que
 * já traz o *focus trap*, o fechamento por `Esc`, o `::backdrop` e o
 * `inert` no resto da página — comportamentos que uma reimplementação em React
 * costuma entregar pela metade, e cuja falta só aparece para quem navega por
 * teclado ou leitor de tela.
 */
export function Modal({
  aberto,
  aoFechar,
  titulo,
  descricao,
  largura = 560,
  /** `false` prende o usuário no diálogo — para quando fechar no meio perde algo. */
  fechavel = true,
  /** Botões fixos no rodapé (fora da rolagem). */
  rodape,
  children,
}: {
  aberto: boolean;
  aoFechar: () => void;
  titulo: string;
  descricao?: ReactNode;
  largura?: number;
  fechavel?: boolean;
  rodape?: ReactNode;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const idTitulo = useId();

  useEffect(() => {
    const dialogo = ref.current;
    if (!dialogo) return;
    // `showModal()` (e não o atributo `open`) é o que ativa o backdrop e o
    // aprisionamento de foco. Chamar duas vezes lança, daí a checagem.
    if (aberto && !dialogo.open) dialogo.showModal();
    if (!aberto && dialogo.open) dialogo.close();
  }, [aberto]);

  useEffect(() => {
    const dialogo = ref.current;
    if (!dialogo) return;
    // O `Esc` do navegador fecha o `<dialog>` sem passar pelo React; sem ouvir
    // o `cancel`, o estado da página continuaria achando que está aberto.
    const aoCancelar = (e: Event) => {
      e.preventDefault();
      if (fechavel) aoFechar();
    };
    dialogo.addEventListener('cancel', aoCancelar);
    return () => dialogo.removeEventListener('cancel', aoCancelar);
  }, [aoFechar, fechavel]);

  return (
    <dialog
      ref={ref}
      aria-labelledby={idTitulo}
      className={css.modal}
      style={{ ['--largura' as string]: `${largura}px` }}
      onClick={(e) => {
        // Clique no backdrop: o alvo é o próprio <dialog>.
        if (fechavel && e.target === ref.current) aoFechar();
      }}
    >
      <header className={css.modalCabecalho}>
        <div>
          <h2 id={idTitulo} className={css.modalTitulo}>
            {titulo}
          </h2>
          {descricao && <p className={css.modalDescricao}>{descricao}</p>}
        </div>
        {fechavel && (
          <button
            type="button"
            className={css.fechar}
            onClick={aoFechar}
            aria-label="Fechar"
          >
            <X />
          </button>
        )}
      </header>
      <div className={css.modalCorpo}>{aberto && children}</div>
      {rodape && <footer className={css.modalRodape}>{rodape}</footer>}
    </dialog>
  );
}
